// The one backoff.
//
// Moved from `packages/github/src/backoff.ts`, which was already the good
// version — it reads `Retry-After` and `x-ratelimit-reset` off a real `Response`
// rather than guessing, and the five other implementations counted by
// `scripts/one-impl.test.ts` did not. A server that TELLS you when to come back
// and a client that ignores it is how a rate limit becomes an outage: every
// client in the fleet retries on the same exponential curve, so they retry
// together, forever.
//
// It lives here rather than in `packages/github` because the math is not
// GitHub's. What IS GitHub's — "is this particular 403 a throttle?" — stays
// there, in `isRateLimited`.

/** Ceiling on a single wait. Past a minute, a caller should be parking work, not sleeping on it. */
export const BACKOFF_CAP_MS = 60_000;

/** Cap on retries for transient failures. */
export const DEFAULT_MAX_RETRIES = 5;

/**
 * Wait time (ms) before the next retry. Honours `Retry-After` and
 * `x-ratelimit-reset` when present, otherwise exponential (1s, 2s, 4s, …),
 * capped at {@link BACKOFF_CAP_MS}.
 *
 * The two header reads are ordered, not merged: `Retry-After` is the server's
 * instruction and outranks a reset time we would otherwise have to date
 * ourselves. `x-ratelimit-reset` is absolute UNIX seconds, so it is only usable
 * against a caller-supplied `nowMs` — passed in rather than read, so a test can
 * prove the arithmetic without owning the clock.
 *
 * `Retry-After` is `delay-seconds | HTTP-date` (RFC 9110 §10.2.3) and BOTH halves
 * are read. A date is dated against the same `nowMs`, so the whole function still
 * owns no clock. Parsing only the numeric half meant the header this module exists
 * to honour was dropped without a trace whenever a server chose the other spelling.
 *
 * ONE rule covers every non-positive answer, from either header: a wait that is not
 * strictly positive falls through to the exponential. A clock that disagrees with
 * the server's by a second, a reset already past, a `Retry-After: 0` and a negative
 * one are the same situation, and none of them may turn a backoff into a spin
 * against a server that just throttled.
 */
export function computeBackoffMs(res: Response, attempt: number, nowMs: number): number {
  const retryAfterMs = parseRetryAfterMs(res.headers.get('retry-after'), nowMs);
  if (retryAfterMs !== null && retryAfterMs > 0) return Math.min(retryAfterMs, BACKOFF_CAP_MS);
  const resetAt = parseNumericHeader(res.headers.get('x-ratelimit-reset'));
  if (resetAt !== null) {
    const ms = resetAt * 1000 - nowMs;
    if (ms > 0) return Math.min(ms, BACKOFF_CAP_MS);
  }
  return exponentialBackoffMs(attempt);
}

/**
 * IMF-fixdate, RFC 9110 §5.6.7 — the one date form `Retry-After` may carry and the
 * only one providers send.
 */
const IMF_FIXDATE = /^[A-Za-z]{3}, \d{2} [A-Za-z]{3} \d{4} \d{2}:\d{2}:\d{2} GMT$/;

/**
 * delta-seconds, RFC 9110 §10.2.3 — `1*DIGIT`, an integer count of seconds.
 *
 * Deliberately NOT {@link parseNumericHeader}, which is `Number()` and so also
 * accepts `1.5`, `+3`, `1e3` and a signed value. That is right for
 * `x-ratelimit-reset`, which is a numeric field rather than this grammar, and wrong
 * here: reading `1.5` as 1500 ms is inventing a wait the server did not state.
 */
const DELTA_SECONDS = /^\d+$/;

/**
 * `Retry-After` as milliseconds to wait, in whichever of its two spellings the
 * server used; `null` when the header is absent or is neither.
 *
 * `Date.parse` is tried only AFTER the numeric read fails, because it accepts bare
 * integers on some engines and would read `120` as a year rather than two minutes.
 * The result may be zero or negative — the caller decides what a non-positive
 * instruction means, so the two grammars cannot answer that question differently.
 *
 * THE SHAPE CHECK IS LOAD-BEARING, not belt-and-braces. `Date.parse` is far looser
 * than the grammar: `Date.parse('Dec 2026')` answers a real timestamp, so handing an
 * unreadable header straight to it invents a wait out of junk — measured here as a
 * garbled header producing the full 60 s cap instead of the 1 s exponential. Only a
 * string shaped like IMF-fixdate is a date; everything else is unreadable, and an
 * unreadable header must mean "the server said nothing".
 *
 * ── TWO IMPLEMENTATIONS EXIST, ON PURPOSE (for now) ──────────────────────────
 * `packages/domain/src/entities/byok/verify-failure.ts` exports its own
 * `parseRetryAfterMs` over this same grammar, consumed by
 * `packages/billing/src/byok.ts`. That is a real duplicate in a package whose own
 * CLAUDE.md says "one implementation each", and it is not an oversight left lying
 * around — it is currently UNFIXABLE without a governance change:
 *
 *   - `scripts/import-boundaries.test.ts` pins BOTH `'packages/domain': []` and
 *     `'packages/kit': []`, each as a "free ratchet", and `docs/idea/monorepo.md`
 *     states both invariants in prose (`domain` line 83, `kit` line 131).
 *   - So neither may import the other, in either direction, and there is no third
 *     L0 home they could share without breaking one of those two pins. Two sibling
 *     roots that import nothing internal CANNOT share code, by construction.
 *
 * Consolidating therefore means re-deciding the dependency graph to de-duplicate a
 * twelve-line pure function, which is the wrong trade today.
 *
 * They agree on the GRAMMAR — which of the two forms a string is, or neither —
 * and that was established by differentially probing both over the same inputs,
 * not by reading them. The probe is also how the divergence that existed when this
 * comment was written got found: this copy read delta-seconds with `Number()` and so
 * accepted `1.5` as 1500 ms where domain refused it. Two copies had already drifted
 * before anyone had finished writing down that there were two.
 *
 * They still differ on the CONTRACT, deliberately and visibly:
 *   - domain's parser only FLOORS at 0 (never a negative); the one-hour ceiling is a
 *     separate step, `verifyRetryDelayMs` in the same file, which bounds the delay to
 *     `[VERIFY_RETRY_BACKOFF_MS, VERIFY_RETRY_MAX_BACKOFF_MS]` because its caller parks
 *     a BYOK key and an unbounded cooldown is how a key is never looked at again (#2086);
 *   - this one returns a signed delta and lets `computeBackoffMs` apply `> 0` and
 *     `BACKOFF_CAP_MS`, because its callers are retry ladders, not a key pool.
 * A merge must reconcile those two, not delete one.
 *
 * TRIP CONDITION — merge them when ANY of these becomes true:
 *   1. a THIRD copy appears (the count is the signal; two is a structural artifact,
 *      three is a pattern nobody is managing);
 *   2. the two diverge on the GRAMMAR again — a fix landing in one and not the other
 *      is the bug this comment exists to make findable, and it has happened once
 *      already;
 *   3. an L-minus-1 package both may import comes to exist for another reason, at
 *      which point this costs one import rather than a graph change.
 * Whoever merges them owns every call site: `computeBackoffMs` below,
 * `retryAfterMs` in `apps/dashboard/src/lib/sseBackoff.ts` (exported for it, #6355:
 * it was about to be the third copy, reading `1e3` as 1000 s and ignoring a date),
 * `retryAfterMs` in `apps/worker/src/consumers/plan-execute-call.ts` (a caller, not a
 * copy: it calls this parser and floors the signed delta at 0),
 * and `verifyRetryDelayMs` in that domain file. Note the contracts are NOT identical —
 * domain's parser floors at 0 and leaves the ceiling to `verifyRetryDelayMs`; this one
 * returns a signed delta and lets `computeBackoffMs` decide. Merging means reconciling that,
 * not deleting one.
 *
 * `scripts/one-impl.test.ts` is green over this pair and always was: its detectors
 * are shape-based (`* 2 **`, `Math.pow(2,`, a `backoff|delay|retry`-named binding)
 * and none of them matches a `Retry-After` parser. A guard being green is evidence
 * about the guard's reach, never about the tree.
 */
export function parseRetryAfterMs(v: string | null, nowMs: number): number | null {
  if (v === null) return null;
  const value = v.trim();
  if (DELTA_SECONDS.test(value)) {
    const ms = Number(value) * 1000;
    return Number.isSafeInteger(ms) ? ms : null;
  }
  if (!IMF_FIXDATE.test(value)) return null;
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : at - nowMs;
}

/**
 * The header-free half, for a caller with no `Response` to read — a NATS dial, a
 * DB reconnect, an SSE re-subscribe.
 *
 * Exported so those callers stop open-coding `Math.min(cap, base * 2 ** attempt)`,
 * which is the shape `scripts/one-impl.test.ts` counts. `attempt` is 0-based, so
 * the first wait is `baseMs`.
 */
export function exponentialBackoffMs(
  attempt: number,
  opts: { baseMs?: number; capMs?: number } = {},
): number {
  const base = opts.baseMs ?? 1000;
  const cap = opts.capMs ?? BACKOFF_CAP_MS;
  return Math.min(base * 2 ** Math.max(0, attempt), cap);
}

/** Parse a numeric HTTP header; null for missing / empty / non-finite. */
export function parseNumericHeader(v: string | null): number | null {
  if (v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
